import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import teamNames from './teamNames.json';

const englishTeamNamePairs = [
  { team1: 'Red Team', team2: 'Blue Team' },
  { team1: 'Gold Team', team2: 'Silver Team' },
  { team1: 'North Team', team2: 'South Team' },
  { team1: 'East Team', team2: 'West Team' },
  { team1: 'The Falcons', team2: 'The Eagles' },
  { team1: 'The Knights', team2: 'The Warriors' },
  { team1: 'The Champions', team2: 'The Challengers' },
  { team1: 'The Stars', team2: 'The Rockets' },
  { team1: 'The Scholars', team2: 'The Thinkers' },
  { team1: 'The Pioneers', team2: 'The Explorers' },
  { team1: 'Team Alpha', team2: 'Team Beta' },
  { team1: 'Team One', team2: 'Team Two' },
];

export default function LandingPage({
  teams,
  setTeams,
  selectedQuestionCount,
  questionBankOptions,
  selectedQuestionBankKey,
  setSelectedQuestionBankKey,
  language,
  text,
  isMuted,
  setIsMuted,
  isOpenPlay,
  setIsOpenPlay,
  openPlaySeconds,
  setOpenPlaySeconds,
  isComputerMode,
  toggleComputerMode,
  selectGameMode,
  startGame,
}) {
  const [showNameModal, setShowNameModal] = useState(false);
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [startStep, setStartStep] = useState('mode');

  const chooseMode = (mode) => {
    selectGameMode(mode);
    setStartStep('setup');
  };

  const suggestNames = () => {
    const pairs = language === 'en' ? englishTeamNamePairs : teamNames.pairs;
    const pair = pairs[Math.floor(Math.random() * pairs.length)];
    setTeams({
      team1: { ...teams.team1, name: pair.team1 },
      team2: { ...teams.team2, name: isComputerMode ? text.teamAiName : pair.team2 },
    });
  };

  const handleStart = () => {
    if (!teams.team1.name.trim() || !teams.team2.name.trim()) {
      setShowNameModal(true);
      return;
    }
    startGame();
  };

  const setupTitle = isComputerMode ? text.setupAiTitle : text.setupTeamsTitle;
  const teamOneLabel = isComputerMode ? text.playerName : text.teamOne;
  const teamOnePlaceholder = isComputerMode ? text.playerPlaceholder : text.teamOnePlaceholder;
  const teamTwoLabel = isComputerMode ? text.aiTeamLabel : text.teamTwo;
  const teamTwoPlaceholder = isComputerMode ? text.teamAiName : text.teamTwoPlaceholder;

  if (startStep === 'mode') {
    return (
      <div dir={text.dir} className="landing-page min-h-screen overflow-x-hidden">
        <div className="landing-wrap mode-wrap">
          <motion.div
            className="mode-hero"
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <img className="mode-logo" src="/mindclash-logo.png" alt={text.brandName} />
            <h1 className="mode-brand">{text.brandName}</h1>
            <p className="mode-subtitle">{text.tagline}</p>
            <div className="gold-line"><span className="gold-diamond" /></div>
            <h2 className="mode-title">{text.chooseChallenge}</h2>
          </motion.div>

          <motion.div
            className="mode-grid"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08, duration: 0.55 }}
          >
            <ModeCard
              index="01"
              tone="red"
              imageSrc="/mode-teams.png"
              title={text.challengeTeams}
              subtitle={text.challengeTeamsSubtitle}
              description={text.challengeTeamsDesc}
              action={text.chooseChallengeAction}
              onClick={() => chooseMode('teams')}
            />
            <ModeCard
              index="02"
              tone="blue"
              imageSrc="/mode-ai.png"
              title={text.challengeAi}
              subtitle={text.challengeAiSubtitle}
              description={text.challengeAiDesc}
              action={text.chooseChallengeAction}
              onClick={() => chooseMode('ai')}
            />
            <ModeCard
              index="03"
              tone="green"
              imageSrc="/mode-remote.png"
              title={text.challengeRemote}
              subtitle={text.challengeRemoteSubtitle}
              description={text.challengeRemoteDesc}
              action={text.chooseChallengeAction}
              onClick={() => setShowRemoteModal(true)}
            />
          </motion.div>

          <section className="settings-panel mode-settings-panel" aria-label={text.gameSettings}>
            <div className="settings-panel-head">
              <h2>{text.gameSettings}</h2>
            </div>


            <div className="question-bank-section">
              <div className="settings-subtitle">{text.chooseQuestionType}</div>
              <div className="question-bank-grid">
                {questionBankOptions.map((bank) => (
                  <QuestionBankCard
                    key={bank.key}
                  bank={bank}
                  active={selectedQuestionBankKey === bank.key}
                  uiDir={text.dir}
                  onClick={() => {
                    if (!bank.disabled) setSelectedQuestionBankKey(bank.key);
                  }}
                />
                ))}
              </div>
            </div>
          </section>

          <AnimatePresence>
            {showRemoteModal && (
              <motion.div
                className="name-modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowRemoteModal(false)}
              >
                <motion.div
                  className="name-modal"
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.85, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="name-modal-icon">🌐</div>
                  <div className="name-modal-title">{text.remoteTitle}</div>
                  <div className="name-modal-desc">
                    {text.remoteDesc}
                    <br />
                    {text.comingSoon}
                  </div>
                  <div className="name-modal-actions">
                    <button type="button" className="name-modal-close" onClick={() => setShowRemoteModal(false)}>
                      {text.ok}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <footer className="landing-footer mode-footer">
            <nav className="help-footer-links" aria-label={language === 'en' ? 'Help and privacy' : 'الدعم والخصوصية'}>
              <a href="/support">{language === 'en' ? 'Support' : 'الدعم والمساعدة'}</a>
              <a href="/privacy">{language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}</a>
            </nav>
            <div className="footer-text">{text.footerMadeBy}</div>
            <div className="footer-name">{text.footerName}</div>
            <a
              className="footer-x-link"
              href="https://x.com/Shaye_Alshaye"
              target="_blank"
              rel="noreferrer"
            >
              <img src="/x-icon.svg" alt="X" className="footer-x-icon" />
              <span>@Shaye_Alshaye</span>
            </a>
            <div className="footer-gold">✦ ✦ ✦</div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div dir={text.dir} className="landing-page min-h-screen overflow-x-hidden">
      <div className="landing-wrap setup-wrap">
        <motion.div className="landing-hero" initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }}>
          <img className="setup-logo" src="/mindclash-logo.png" alt={text.brandName} />
          <div className="setup-brand-subtitle">{text.brandName}</div>
          <div className="gold-line"><span className="gold-diamond" /></div>
          <h1 className="setup-mode-title">{setupTitle}</h1>
        </motion.div>

        {isComputerMode && (
          <div className="ai-challenge-panel">
            <div className="ai-challenge-icon">🤖</div>
            <div>
              <div className="ai-challenge-title">{text.aiChallengeTitle}</div>
              <div className="ai-challenge-copy">{text.aiChallengeCopy}</div>
            </div>
          </div>
        )}

        <motion.div className="teams-row" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="team-card red">
            <div className="team-label">
              <span className="team-name">{teamOneLabel}</span>
              <span className="team-icon-box">🔴</span>
            </div>
            <input
              className="team-input"
              value={teams.team1.name}
              onChange={e => setTeams({ ...teams, team1: { ...teams.team1, name: e.target.value } })}
              placeholder={teamOnePlaceholder}
            />
          </div>

          <div className="vs-wrap">
            <div className="medal medal-sm"><span className="medal-txt">{text.versus}</span></div>
            <div className="medal-stars"><span>★</span><span>★</span><span>★</span></div>
            {!isComputerMode && (
            <div className="suggest-row suggest-row-desktop">
              <button type="button" className="suggest-btn suggest-btn-sm" onClick={suggestNames}>
                <span className="suggest-ico">🎲</span>
                <span>{text.suggestNames}</span>
              </button>
            </div>
            )}
          </div>

          <div className="team-side-col">
            {false && (
            <ToggleButton
            active={isComputerMode}
            icon="🤖"
            title={text.challengeAi}
            description={text.aiChallengeCopy}
            onClick={toggleComputerMode}
            compact
          />
            )}

            <div className="team-card blue">
              <div className="team-label">
                <span className="team-name">{teamTwoLabel}</span>
                <span className="team-icon-box">{isComputerMode ? '🤖' : '🔵'}</span>
              </div>
              <input
                className="team-input"
                value={teams.team2.name}
                disabled={isComputerMode}
                onChange={e => setTeams({ ...teams, team2: { ...teams.team2, name: e.target.value } })}
                placeholder={teamTwoPlaceholder}
              />
            </div>
          </div>

          {!isComputerMode && (
          <div className="suggest-row suggest-row-mobile">
            <button type="button" className="suggest-btn suggest-btn-sm" onClick={suggestNames}>
              <span className="suggest-ico">🎲</span>
              <span>{text.suggestNames}</span>
            </button>
          </div>
          )}
        </motion.div>

        <div className="info-bar">
          <div className="info-item"><span className="info-ico">📚</span>{text.questionCount} <strong>{selectedQuestionCount}</strong></div>
          <div className="info-item"><span className="info-ico">🔁</span>{text.noRepeat}</div>
          <div className="info-item"><span className="info-ico">⚖️</span>{text.randomCategory}</div>
          <div className="info-item"><span className="info-ico">⏱️</span>{text.questionDuration} <strong>{isOpenPlay ? openPlaySeconds : 40} {text.secondsUnit}</strong></div>
        </div>

        <section className="settings-panel" aria-label={text.gameSettings}>
          <div className="settings-panel-head">
            <h2>{text.gameSettings}</h2>
          </div>

          <div className="game-settings">
            <ToggleButton
              active={isMuted}
              icon={isMuted ? '🔇' : '🔊'}
              title={text.muteSound}
              description={text.soundEffects}
              onClick={() => setIsMuted(prev => !prev)}
            />
            {!isComputerMode && (
            <ToggleButton
              active={isOpenPlay}
              icon="👁️"
              title={text.openPlay}
              description={text.openPlayDesc}
              onClick={() => setIsOpenPlay(prev => !prev)}
            />
            )}
          </div>
        </section>

        {!isComputerMode && isOpenPlay && (
          <div className="open-play-time" aria-label={text.openPlayTime}>
            <span className="open-play-time-label">{text.openPlayTime}</span>
            <div className="open-play-time-options">
              {[10, 15, 20].map(seconds => (
                <button
                  key={seconds}
                  type="button"
                  className={`time-option ${openPlaySeconds === seconds ? 'active' : ''}`}
                  onClick={() => setOpenPlaySeconds(seconds)}
                  aria-pressed={openPlaySeconds === seconds}
                >
                  {text.secondsOption(seconds)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="start-wrap">
          <button type="button" className="change-mode-btn" onClick={() => setStartStep('mode')}>
            {text.changeChallenge}
          </button>
          <button className="start-btn" onClick={handleStart}>
            <span className="start-ico">▶</span>
            <span>{text.startContest}</span>
          </button>
        </div>

        <AnimatePresence>
          {showNameModal && (
            <motion.div
              className="name-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNameModal(false)}
            >
              <motion.div
                className="name-modal"
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.85, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="name-modal-icon">⚠️</div>
                <div className="name-modal-title">{text.missingNamesTitle}</div>
                <div className="name-modal-desc">{text.missingNamesDesc}</div>
                <div className="name-modal-actions">
                  <button
                    type="button"
                    className="suggest-btn"
                    onClick={() => { suggestNames(); setShowNameModal(false); }}
                  >
                    <span className="suggest-ico">🎲</span>
                    <span>{text.suggestNames}</span>
                  </button>
                  <button type="button" className="name-modal-close" onClick={() => setShowNameModal(false)}>
                    {text.ok}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <footer className="landing-footer">
          <nav className="help-footer-links" aria-label={language === 'en' ? 'Help and privacy' : 'الدعم والخصوصية'}>
            <a href="/support">{language === 'en' ? 'Support' : 'الدعم والمساعدة'}</a>
            <a href="/privacy">{language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}</a>
          </nav>
          <div className="footer-text">{text.footerMadeBy}</div>
          <div className="footer-name">{text.footerName}</div>
          <a
            className="footer-x-link"
            href="https://x.com/Shaye_Alshaye"
            target="_blank"
            rel="noreferrer"
          >
            <img src="/x-icon.svg" alt="X" className="footer-x-icon" />
            <span>@Shaye_Alshaye</span>
          </a>
          <div className="footer-gold">✦ ✦ ✦</div>
        </footer>
      </div>
    </div>
  );
}

function ModeCard({ index, tone, imageSrc, title, subtitle, description, action, onClick }) {
  return (
    <button type="button" className={`mode-card ${tone}`} onClick={onClick}>
      <span className="mode-card-image-wrap">
        <img className="mode-card-image" src={imageSrc} alt="" aria-hidden="true" />
      </span>
      <span className="mode-card-copy">
        <span className="mode-card-head">
          <span className="mode-card-title">{title}</span>
          <span className="mode-card-index">{index}</span>
        </span>
        <span className="mode-card-subtitle">{subtitle}</span>
        <span className="mode-card-description">{description}</span>
        <span className="mode-card-action">{action}</span>
      </span>
    </button>
  );
}

function QuestionBankCard({ bank, active, uiDir, onClick }) {
  return (
    <button
      type="button"
      className={`question-bank-card ${active ? 'active' : ''} ${bank.disabled ? 'disabled' : ''}`}
      onClick={onClick}
      aria-pressed={active}
      disabled={bank.disabled}
      dir={uiDir === 'ltr' ? 'ltr' : (bank.dir || 'rtl')}
    >
      <span className="question-bank-head">
        <span className="question-bank-title">{bank.title}</span>
        <span className="question-bank-language">{bank.languageLabel}</span>
      </span>
      <span className="question-bank-count">{bank.countText}</span>
      <span className="question-bank-desc">{bank.description}</span>
    </button>
  );
}

function ToggleButton({ active, icon, title, description, onClick, compact = false }) {
  return (
    <button
      type="button"
      className={`setting-toggle ${compact ? 'compact' : ''} ${active ? 'active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span className="setting-icon">{icon}</span>
      <span className="setting-copy">
        <span className="setting-title">{title}</span>
        <span className="setting-desc">{description}</span>
      </span>
      <span className="setting-switch" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}
